import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Card,
  CardContent,
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridPaginationModel,
} from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { Cliente, ClienteStatus } from '@/types/cliente'
import { formatCNPJ, formatCurrency, formatDate } from '@/utils/formatters'
import { cnpjContemTermo } from '@/utils/cnpj'
import { clienteService } from '@/services/clienteService'
import { tokens } from '@/theme'

const statusColorMap: Record<ClienteStatus, 'success' | 'warning' | 'info' | 'error'> = {
  ATIVO: 'success',
  INATIVO: 'error',
  PROSPECT: 'info',
}

const statusLabelMap: Record<ClienteStatus, string> = {
  ATIVO: 'Ativo',
  INATIVO: 'Inativo',
  PROSPECT: 'Prospect',
}

/**
 * Lista de clientes. `embutida` (Onda 2 / A04): renderizada como aba de
 * "Operações e Clientes", que já traz PageHeader e KPIs — o cabeçalho local sai.
 */
const ClientesListPage: React.FC<{ embutida?: boolean }> = ({ embutida = false }) => {
  const navigate = useNavigate()
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [searchText, setSearchText] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<ClienteStatus | 'TODOS'>('TODOS')
  const [setorFilter, setSetorFilter] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  })
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [clienteToDelete, setClienteToDelete] = useState<Cliente | null>(null)

  const carregarClientes = useCallback(async () => {
    setLoading(true)
    setErro(null)
    try {
      const page = await clienteService.listar(paginationModel.page, paginationModel.pageSize)
      setClientes(page.content ?? [])
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar clientes')
      setClientes([])
    } finally {
      setLoading(false)
    }
  }, [paginationModel.page, paginationModel.pageSize])

  useEffect(() => {
    carregarClientes()
  }, [carregarClientes])

  const setores = useMemo(
    () => [...new Set(clientes.map((c) => c.setorIndustria))],
    [clientes],
  )

  const filteredClientes = useMemo(() => {
    return clientes.filter((cliente) => {
      const matchesSearch =
        cliente.razaoSocial.toLowerCase().includes(searchText.toLowerCase()) ||
        cnpjContemTermo(cliente.cnpj, searchText)

      const matchesStatus =
        statusFilter === 'TODOS' || cliente.status === statusFilter

      const matchesSetor =
        setorFilter === '' || cliente.setorIndustria === setorFilter

      return matchesSearch && matchesStatus && matchesSetor
    })
  }, [clientes, searchText, statusFilter, setorFilter])

  const handleEditCliente = useCallback(
    (cliente: Cliente) => {
      navigate(`/clientes/${cliente.id}/editar`, { state: { cliente } })
    },
    [navigate],
  )

  const handleViewCliente = useCallback(
    (clienteId: string) => {
      navigate(`/clientes/${clienteId}`)
    },
    [navigate],
  )

  const handleDeleteClick = useCallback((cliente: Cliente) => {
    setClienteToDelete(cliente)
    setDeleteConfirmOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!clienteToDelete) return
    setLoading(true)
    try {
      await clienteService.deletar(clienteToDelete.id)
      setClientes((prev) => prev.filter((c) => c.id !== clienteToDelete.id))
      setDeleteConfirmOpen(false)
      setClienteToDelete(null)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao arquivar cliente')
    } finally {
      setLoading(false)
    }
  }, [clienteToDelete])

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false)
    setClienteToDelete(null)
  }, [])

  const columns: GridColDef[] = [
    {
      field: 'razaoSocial',
      headerName: 'Razão Social',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          component="span"
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
          onClick={() => handleViewCliente(params.row.id)}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'cnpj',
      headerName: 'CNPJ',
      width: 160,
      renderCell: (params: GridRenderCellParams) => formatCNPJ(params.value),
    },
    {
      field: 'tipo',
      headerName: 'Tipo',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
          size="small"
          variant="outlined"
          color={params.value === 'PJ' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'setorIndustria',
      headerName: 'Setor',
      width: 150,
    },
    {
      field: 'faturamentoAnual',
      headerName: 'Faturamento Anual',
      width: 160,
      renderCell: (params: GridRenderCellParams) =>
        formatCurrency(params.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={statusLabelMap[params.value as ClienteStatus]}
          color={statusColorMap[params.value as ClienteStatus]}
          size="small"
        />
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Última Atualização',
      width: 150,
      renderCell: (params: GridRenderCellParams) =>
        formatDate(params.value),
    },
    {
      field: 'acoes',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={1}>
          <EditIcon
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              fontSize: 20,
              '&:hover': { opacity: 0.7 },
            }}
            onClick={() => handleEditCliente(params.row)}
          />
          <DeleteIcon
            sx={{
              cursor: 'pointer',
              color: 'error.main',
              fontSize: 20,
              '&:hover': { opacity: 0.7 },
            }}
            onClick={() => handleDeleteClick(params.row)}
          />
        </Stack>
      ),
    },
  ]

  return (
    <Box>
      {/* Header — some quando a lista vive dentro de "Operações e Clientes" (A04) */}
      {!embutida && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Clientes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Os clientes são criados a partir de uma reserva.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/reservas/nova')}
          >
            Nova oportunidade
          </Button>
        </Stack>
      )}

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível carregar os clientes. {erro}
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            placeholder="Buscar por nome ou CNPJ..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />

          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ClienteStatus | 'TODOS')}
              label="Status"
            >
              <MenuItem value="TODOS">Todos</MenuItem>
              <MenuItem value="ATIVO">Ativo</MenuItem>
              <MenuItem value="INATIVO">Inativo</MenuItem>
              <MenuItem value="PROSPECT">Prospect</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Setor</InputLabel>
            <Select
              value={setorFilter}
              onChange={(e) => setSetorFilter(e.target.value)}
              label="Setor"
            >
              <MenuItem value="">Todos</MenuItem>
              {setores.map((setor) => (
                <MenuItem key={setor} value={setor}>
                  {setor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* DataGrid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        {filteredClientes.length === 0 ? (
          <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                Nenhum cliente encontrado
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Os clientes são criados a partir de uma reserva. Clique em "Nova Reserva" para começar.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <DataGrid
            rows={filteredClientes}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25]}
            loading={loading}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid ' + tokens.divider,
              },
            }}
          />
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja arquivar o cliente{' '}
            <strong>{clienteToDelete?.razaoSocial}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Arquivar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ClientesListPage
